// Pilot access for the Leads page (/app/leads), plus the single shared fetch of
// GET /los/leads that both the navigation check and the page itself consume.
//
// There is no separate "am I allowed" endpoint: the leads endpoint's own status
// is the answer, the way the Ads tab reads its 404 (Ads/ads-client.ts,
// Ads/ads-model.ts). 403 means this LO has not been granted the pilot. The
// backend gate is the real one — everything here only decides what to draw.
//
// The in-memory cache dedups the boot-time nav check against the page's own load
// so opening the portal and then clicking Leads does not run the merged
// ads+organic query twice.

const CACHE_TTL_MS = 60_000

let cache = null
const inFlightByToken = new Map()

/**
 * Fetch GET /los/leads once, returning { status, body }. A result under
 * CACHE_TTL_MS old is reused; concurrent callers share one in-flight request.
 * Never throws: a network failure resolves to { status: 0, body: null } so
 * callers branch on status alone.
 */
export async function loadLeads(now = Date.now()) {
  const token = sessionStorage.getItem("ZeitroA")
  if (cache && cache.token === token && now - cache.at < CACHE_TTL_MS) {
    return cache.result
  }
  const existingRequest = inFlightByToken.get(token)
  if (existingRequest) {
    return existingRequest
  }
  const request = (async () => {
    let result
    try {
      const res = await fetch("/los/leads", {
        method: "GET",
        headers: { Authorization: "Bearer " + token },
        cache: "no-store",
      })
      let body = null
      try {
        body = await res.json()
      } catch {
        body = null
      }
      result = { status: res.status, body }
    } catch {
      result = { status: 0, body: null }
    }
    cache = { token, at: now, result }
    return result
  })()
  inFlightByToken.set(token, request)
  const clearRequest = () => {
    if (inFlightByToken.get(token) === request) inFlightByToken.delete(token)
  }
  request.then(clearRequest, clearRequest)
  return request
}

/** Test/logout helper: drop the cached result. */
export function clearLeadsCache() {
  cache = null
  inFlightByToken.clear()
}

/**
 * Map the status of GET /los/leads to a navigation state. 403 is the pilot gate
 * answering "not you" and is a normal reply, not an error; 401 is an expired
 * session; anything else that is not a 200 is a temporary outage.
 */
export function interpretLeadsAccessStatus(status) {
  if (status === 200) return "enabled"
  if (status === 403) return "notEnabled"
  if (status === 401) return "unauthorized"
  return "unavailable"
}

/**
 * Whether to draw the Leads entry.
 *
 * FAILS CLOSED, and this is the one deliberate difference from
 * shouldShowAdsNav: Ads keeps its entry through an outage so the page can
 * explain itself, but Leads is mid-pilot, and showing the entry to every LO in
 * the company whenever the backend hiccups is the exact thing the gate exists to
 * prevent. The cost is that a pilot LO loses the entry during an outage and gets
 * it back on the next load — which is recoverable, unlike the disclosure.
 *
 * At GA this becomes `state !== "notEnabled"`, or the caller stops asking.
 */
export function shouldShowLeadsNav(state) {
  return state === "enabled"
}
